<template>
    <v-container fluid class="main-container d-flex flex-column pa-4">
        <v-card class="d-flex flex-column flex-grow-1">
            <v-card-title class="pa-4 d-flex align-center justify-center">
                <v-icon :icon="icons.mdiRocketLaunchOutline" class="ma-2" color="primary" />
                <span class="font-weight-bold ma-2 text-primary">{{ $t('menu.jobs') }}</span>

                <v-spacer />

                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    :prepend-icon="icons.mdiLightningBolt" @click="onManualExecution">{{ $t('action.manualExecution')
                    }}</v-btn>
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    :prepend-icon="icons.mdiHistory" @click="onHistory">{{ $t('action.history') }}</v-btn>
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    :prepend-icon="icons.mdiFolderMultipleOutline" @click="onServerFiles">{{ $t('action.serverFiles')
                    }}</v-btn>
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    :prepend-icon="icons.mdiCogOutline" @click="onServerConfigFiles">{{ $t('action.serverConfigFiles')
                    }}</v-btn>
            </v-card-title>

            <v-container fluid class="tab-grid pa-0">
                <v-tabs v-model="tabComponent" color="secondary" grow>
                    <v-tab value="schedulerTab" :prepend-icon="icons.mdiChartTimelineVariantShimmer">{{ $t('tab.scheduler') }}</v-tab>
                    <v-tab value="activityTab" :prepend-icon="icons.mdiDockWindow">{{ $t('tab.activity') }}</v-tab>
                    <v-tab value="logBatchesTab" :prepend-icon="icons.mdiMenuOpen">{{ $t('tab.logBatches') }}</v-tab>
                </v-tabs>

                <v-tabs-window style="min-height: 0" v-model="tabComponent">
                    <v-tabs-window-item class="fill-height" value="schedulerTab" eager>
                        <SchedulerTab ref="schedulerTabRef" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="activityTab">
                        <ActivityTab ref="activityTabRef" />
                    </v-tabs-window-item>

                    <v-tabs-window-item class="fill-height" value="logBatchesTab">
                        <LogBatchesTab ref="logBatchesTabRef" :customers="customers" />
                    </v-tabs-window-item>
                </v-tabs-window>
            </v-container>
        </v-card>
            <ManualExecutionDialog ref="manualExecutionDialogRef" :customers="customers" @execution-launched="onExecutionLaunched" />
            <ManualExecutionHistoryDialog ref="manualExecutionHistoryDialogRef" @execution-launched="onExecutionLaunched" />
            <ServerFilesDialog ref="serverFilesDialogRef" :customers="customers" />
            <ServerFilesDialog ref="serverConfigFilesDialogRef" :customers="customers" is-config />
    </v-container>
</template>

<script setup>
import { inject, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue';
import SchedulerTab from '@/views/tabs/Jobs/SchedulerTab.vue';
import ActivityTab from '@/views/tabs/Jobs/ActivityTab.vue';
import LogBatchesTab from '@/views/tabs/Jobs/LogBatchesTab.vue';
import ManualExecutionDialog from '@/views/dialogs/Jobs/ManualExecutionDialog.vue';
import ManualExecutionHistoryDialog from '@/views/dialogs/Jobs/ManualExecutionHistoryDialog.vue';
import ServerFilesDialog from '@/views/dialogs/Jobs/ServerFilesDialog.vue';
import { toast } from 'vue3-toastify';
import { useLogStore } from '@/stores/logStore';
import { useUserStore } from '@/stores/userStore';
import {
    mdiLightningBolt,
    mdiHistory,
    mdiFolderMultipleOutline,
    mdiCogOutline,
    mdiChartTimelineVariantShimmer,
    mdiDockWindow,
    mdiMenuOpen,
    mdiRocketLaunchOutline
} from '@mdi/js';

const urlWebAPI = inject('urlWebAPI');
const translate = inject('translate');
const logStore = useLogStore();
const userStore = useUserStore();

const schedulerTabRef = ref(null);
const activityTabRef = ref(null);
const logBatchesTabRef = ref(null);
const manualExecutionDialogRef = ref(null);
const manualExecutionHistoryDialogRef = ref(null);
const serverFilesDialogRef = ref(null);
const serverConfigFilesDialogRef = ref(null);

const tabComponent = ref('schedulerTab');
const customers = ref ([]);

const icons = {
    mdiRocketLaunchOutline,
    mdiLightningBolt,
    mdiHistory,
    mdiFolderMultipleOutline,
    mdiCogOutline,
    mdiChartTimelineVariantShimmer,
    mdiDockWindow,
    mdiMenuOpen,
};

watch(tabComponent, (value, oldValue) => {
    if (oldValue == 'activityTab')
        activityTabRef.value?.onDeactivate();

    nextTick(() => {
        if (value == 'activityTab')
            activityTabRef.value?.onActivate();

        else if (value == 'logBatchesTab')
            logBatchesTabRef.value?.onActivate();
        else if (value == 'schedulerTab'){
            schedulerTabRef.value?.onActivate();
        }
    });
    localStorage.setItem('activeJobsTab', value);
});

onMounted(() => {
    console.log('change active tab');
    //composition API is too fast now
    //we need to delay the display of activeTab after customers have been binded
    getCustomers().then(() => {
        tabComponent.value = localStorage.getItem('activeJobsTab') ?? tabComponent.value;
    })
});

function getCustomers() {
    let data = {
        token: userStore.getUser().token,
        distributorId: userStore.getUser().distributorId,
    };

    return $.getData(urlWebAPI + 'GetCustomers', data)
        .then((data) => {
            data.unshift({ id: 0, name: translate('all', 'toast'), reference: 'all' });
            customers.value = data;
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing GetCustomers data.');
        });
}

function onManualExecution() {
    manualExecutionDialogRef.value.open();
}

function onHistory() {
    manualExecutionHistoryDialogRef.value.open();
}

function onServerFiles() {
    serverFilesDialogRef.value.open();
}

function onServerConfigFiles() {
    serverConfigFilesDialogRef.value.open();
}

function onExecutionLaunched(launch) {
    let user = userStore.getUser();
    launch['user'] = user.firstName + ' ' + user.lastName;
    launch['createdDate'] = new Date();

    let data = {
        manualExecution: launch,
        token: user.token,
    };

    $.postData(urlWebAPI + 'UpdateManualExecutionHistory', data)
        .then(() => {
            manualExecutionHistoryDialogRef.value.triggerRefresh();
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing UpdateManualExecutionHistory data.');
        });

    tabComponent.value = 'activityTab';
}
</script>
